/**
 * Managed content: banners, content pages and news.
 *
 * These three resources are served from `/api/admin` (see
 * `adminUrl` in `src/utils/url.ts`) and are app-wide rather than per store:
 * one banner set, one page set and one news feed are shown to every client of
 * the mobile app, whichever hub they belong to.
 */

import { FileView } from "./file";

/** The surfaces of the client app a banner or a content page can target. */
export const PAGE_TYPES = [
  "HOMEPAGE",
  "FAQS",
  "ABOUT_US",
  "TERMS_AND_CONDITIONS",
  "PRIVACY_POLICY",
] as const;

export type PageType = (typeof PAGE_TYPES)[number];

/**
 * The page types a content page can be written for.
 *
 * `HOMEPAGE` is missing on purpose: the client app queries content pages only
 * for these four (about us, FAQs and the two policies). A homepage content page
 * would be stored and never read.
 */
export const EDITABLE_CONTENT_PAGE_TYPES = [
  "ABOUT_US",
  "FAQS",
  "TERMS_AND_CONDITIONS",
  "PRIVACY_POLICY",
] as const;

export type EditableContentPageType =
  (typeof EDITABLE_CONTENT_PAGE_TYPES)[number];

/**
 * The page the client app fetches banners for. It asks for this one only, so a
 * banner written against any other page type is never shown.
 */
export const BANNER_PAGE_TYPE: PageType = "HOMEPAGE";

/**
 * About us is not a list.
 *
 * The app reads `items[0]` of the ABOUT_US page and renders its image, title
 * and description as the page header and intro — the remaining items, and the
 * page's own title and description, are never read. So it is edited as one
 * block and written as a single item.
 */
export const SINGLE_BLOCK_PAGE_TYPE: EditableContentPageType = "ABOUT_US";

/** The page types whose items the app renders as an accordion of text. */
export const ACCORDION_PAGE_TYPES: EditableContentPageType[] = [
  "FAQS",
  "TERMS_AND_CONDITIONS",
  "PRIVACY_POLICY",
];

export const PAGE_TYPE_LABELS: Record<PageType, string> = {
  HOMEPAGE: "Página inicial",
  FAQS: "Perguntas frequentes",
  ABOUT_US: "Sobre nós",
  TERMS_AND_CONDITIONS: "Termos e condições",
  PRIVACY_POLICY: "Política de privacidade",
};

export const BANNER_PLACEMENTS = ["TOP", "MIDDLE", "BOTTOM"] as const;

export type BannerPlacement = (typeof BANNER_PLACEMENTS)[number];

export const BANNER_PLACEMENT_LABELS: Record<BannerPlacement, string> = {
  TOP: "Topo",
  MIDDLE: "Meio",
  BOTTOM: "Fundo",
};

/**
 * The placements the client app actually renders: `TOP` in the home carousel,
 * `MIDDLE` as the single app banner on home, agenda and pet history. `BOTTOM`
 * is filtered out by every screen, so a banner placed there is invisible — it
 * stays in the labels above so an existing row still reads properly, but it is
 * not offered for new ones.
 */
export const RENDERED_BANNER_PLACEMENTS = ["TOP", "MIDDLE"] as const;

/**
 * The placement whose banner is drawn as a bare tappable image: the app takes
 * only the image and the link, so a title, subtitle or button text set here
 * would never appear.
 */
export const IMAGE_ONLY_PLACEMENT: BannerPlacement = "MIDDLE";

/** The page metadata every admin collection returns beside its content. */
export interface ContentPageMetadata {
  size: number;
  number: number;
  totalElements: number;
  totalPages: number;
}

export interface ContentPagedResponse<T> {
  content: T[];
  page: ContentPageMetadata;
}

export interface ContentListParams {
  page?: number;
  size?: number;
  signal?: AbortSignal;
}

export interface BannerView {
  id: number;
  file?: FileView | null;
  title?: string | null;
  subtitle?: string | null;
  pageType?: PageType | null;
  placement?: BannerPlacement | null;
  buttonText?: string | null;
  buttonUrl?: string | null;
  /** Read only: stored by older rows, rendered nowhere in the client app. */
  buttonAlt?: string | null;
  createdOn?: string | null;
  updatedOn?: string | null;
}

/**
 * The write shape. Only `fileId` and `pageType` are required by the API — the
 * Hubs form marks every field required, but enforcing that here would reject
 * banners the backend accepts (an image with no button, for instance).
 */
export interface BannerWriteData {
  fileId: number;
  pageType: PageType;
  placement: BannerPlacement;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  buttonUrl?: string;
}

export interface BannerListParams extends ContentListParams {
  pageType?: PageType;
}

export interface ContentPageItemView {
  id?: number | null;
  file?: FileView | null;
  title?: string | null;
  description?: string | null;
  sortOrder?: number | null;
}

export interface ContentPageView {
  id: number;
  pageType?: PageType | null;
  title?: string | null;
  description?: string | null;
  items: ContentPageItemView[];
  createdOn?: string | null;
  updatedOn?: string | null;
}

export interface ContentPageItemWriteData {
  title: string;
  description: string;
  fileId?: number;
  sortOrder?: number;
}

/** `items` must carry at least one entry; the API rejects an empty array. */
export interface ContentPageWriteData {
  pageType: PageType;
  title?: string;
  description?: string;
  items: ContentPageItemWriteData[];
}

export interface ContentPageListParams extends ContentListParams {
  pageType?: PageType;
}

/**
 * `shareUrl` is optional on purpose: `NewsDTO` accepts one, but `NewsView` does
 * not declare it, so a row may come back without the value that was written.
 * The listing renders a dash rather than assuming it is there.
 */
export interface NewsView {
  id: number;
  title?: string | null;
  content?: string | null;
  file?: FileView | null;
  shareUrl?: string | null;
  createdOn?: string | null;
  updatedOn?: string | null;
}

/**
 * `shareUrl` is absent on purpose. The API accepts it and never returns it, and
 * the app builds its own deep link from the article id, so anything written
 * there is unreachable from both ends.
 */
export interface NewsWriteData {
  title: string;
  content: string;
  fileId?: number;
}

export type NewsListParams = ContentListParams;
